import {Component, ElementRef, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {AttributeValueService} from "../../../../service/attribute-value.service";
import {ProductService} from "../../../../service/product.service";
import {SnackBarUtil} from "../../../../qr-copy-message/snack-bar-util.service";
import {ImageService} from "../../../../service/image.service";
import {ProductAvailability} from "../product-availability.enum";
import {MockupDataModel} from "../../../../qr-copy-message/mockup-data.model";
import {ToggleElModel} from "../../../../layouts/toggle/toggle-el.model";
import {CK_EDITOR, CK_EDITOR_CONFIG} from "../../../../constants/ck-editor.constant";
import {LanguageVariations} from "../../../../layouts/language-selector/language-uppercase.enum";
import {Language} from "../../../../models/language";
import {COLOR} from "../../../../constants/attribute.constant";
import {AttributeValueResponse} from "../../../../models/attributeValueResponse";
import {ProductRequest} from "../../../../models/productRequest";
import {SharedConstants} from "../../../../utilityClass/sharedConstants";
import {ProductTranslation} from "../../../../models/productTranslation";
import {ProductEditRequest} from "../../../../models/payment/productEditRequest";
import {ProductImagesRequest} from "../../../../models/productImagesRequest";
import {ActivatedRoute, Router} from "@angular/router";
import {
  CREATE_ERROR_MESSAGE,
  CREATE_SUCCESS_MESSAGE,
  EDIT_ERROR_MESSAGE,
  EDIT_PARTIAL_SUCCESS_MESSAGE,
  EDIT_SUCCESS_MESSAGE,
  INVALID_FORM_MESSAGE,
  LOAD_PRODUCT_DATA_ERROR_MESSAGE,
  MAX_PRODUCT_GALLERY_LENGTH,
  MAX_SELECTED_COLORS
} from "./admin-product-common.factory";

@Component({
  selector: 'app-admin-product-manage',
  templateUrl: './admin-product-manage.component.html',
  styleUrls: ['../admin-products-common.component.scss']
})
export class AdminProductManageComponent {

  protected isEditProductPage: boolean = false;
  protected isVisibilityActive: boolean = true;
  protected showImageSizeError: boolean = false;
  protected isSaveButtonDisabled: boolean = false;

  protected productGalleryMap: Map<string, File> = new Map<string, File>();

  protected stepperLanguageOptions: Map<number, string> = new Map<number, string>(
    [
      [0, Language.HY],
      [1, Language.RU],
      [2, Language.EN],
    ]
  );
  protected selectedLanguageOption: Language = Language.HY;

  protected colorAttributeValues: Array<AttributeValueResponse> = [];
  protected selectedAttributeValues: Array<AttributeValueResponse> = [];
  protected lastSelectedColor?: AttributeValueResponse;

  protected addProductFormGroup: FormGroup;

  private editProductId: bigint = BigInt(0);
  protected productToEdit?: ProductRequest;
  private productRequest?: ProductRequest;

  @ViewChild("topOfForm") topOfForm!: ElementRef<HTMLDivElement>;

  constructor(
    private attributeValueService: AttributeValueService,
    private fb: FormBuilder,
    private productService: ProductService,
    private snackBarUtil: SnackBarUtil,
    private imageService: ImageService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.addProductFormGroup = this.createFormGroup();

    this.route.queryParams.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.editProductId = BigInt(id);
        this.isEditProductPage = true;
        this.initProduct();
      } else {
        this.isEditProductPage = false;
      }
    })
  }

  ngOnInit() {
    this.initAllColorAttributeValues();
  }

  get productNameErrorMessage() {
    const productNameControl = this.getFormControlNameByNameAndCurrentLang('productName');
    const invalidValidatorName = this.getInvalidValidatorNameByFormControl(productNameControl);
    switch (invalidValidatorName) {
      case SharedConstants.REQUIRED:
        return 'Enter product title.'
      case SharedConstants.MIN_LENGTH:
      case SharedConstants.MAX_LENGTH:
        return 'Product title Must be between 2-60 characters long.';
      default:
        return null;
    }
  }


  get priceErrorMessage() {
    const invalidValidatorName = this.getInvalidValidatorName('price');
    switch (invalidValidatorName) {
      case SharedConstants.REQUIRED:
        return 'Enter price.'
      default:
        return null;
    }
  }

  get descriptionErrorMessage() {
    const productDescriptionControl = this.getFormControlNameByNameAndCurrentLang('productDescription');
    const invalidValidatorName = this.getInvalidValidatorNameByFormControl(productDescriptionControl);
    switch (invalidValidatorName) {
      case SharedConstants.MAX_LENGTH:
        return 'Max 1000 characters long.';
      default:
        return null;
    }
  }

  get compatibilityDescriptionErrorMessage() {
    const compatibilityDescriptionControl = this.getFormControlNameByNameAndCurrentLang('compatibilityDescription');
    const invalidValidatorName = this.getInvalidValidatorNameByFormControl(compatibilityDescriptionControl);
    switch (invalidValidatorName) {
      case SharedConstants.MAX_LENGTH:
        return 'Max 600 characters long.';
      default:
        return null;
    }
  }

  get productGalleryUrls(): string[] {
    return Array.from(this.productGalleryMap.keys());
  }

  get productGalleryFiles(): File[] {
    return Array.from(this.productGalleryMap.values());
  }

  protected redirectToProducts(then?: () => void) {
    this.router.navigateByUrl('admin/page/shop/products').then(then)
  }

  protected onSubmitForm() {
    if (this.isSaveButtonDisabled || this.addProductFormGroup.invalid || (this.productGalleryFiles.length == 0 && !this.productToEdit?.imageUrls?.length)) {
      this.showSnackbar(INVALID_FORM_MESSAGE, SnackBarUtil.CLASSES.error);
      this.scrollToSteps();
      this.updateStepByInvalidTranslationGroup();
      return;
    }

    const isSaveProductPage = !this.isEditProductPage;
    if (isSaveProductPage) {
      this.saveProduct();
    } else {
      this.editProduct();
    }

  }

  getNameWithCurrentLanguage(name: string) {
    return `${name} / ${this.getCurrentLanguageFormGroup()}`;
  }

  private saveProduct() {
    this.productRequest = this.addProductFormGroup.getRawValue() as ProductRequest;
    this.isSaveButtonDisabled = true;
    this.productService.save(this.productRequest, this.productGalleryFiles).subscribe({
      next: () => {
        this.redirectToProducts(() => {
          this.showSnackbar(CREATE_SUCCESS_MESSAGE, SnackBarUtil.CLASSES.success);
        })
        this.isSaveButtonDisabled = false;
      },
      error: () => {
        this.isSaveButtonDisabled = false;
        this.showSnackbar(CREATE_ERROR_MESSAGE, SnackBarUtil.CLASSES.error);
      }
    });
  }

  private editProduct() {
    const newImages: Array<File> = this.productToEdit?.newImages || [];
    const deletedImages: Array<string> = this.productToEdit?.deletedImages || [];
    const request = this.addProductFormGroup.getRawValue() as ProductEditRequest;

    if (this.productToEdit) {
      this.productService.edit(this.editProductId, request).subscribe({
        next: () => {
          if (newImages.length || deletedImages.length) {
            this.handleImageUpdates(newImages, deletedImages);
          } else {
            this.completeEdit(EDIT_SUCCESS_MESSAGE);
          }
        },
        error: () => {
          this.showSnackbar(EDIT_ERROR_MESSAGE, SnackBarUtil.CLASSES.error);
        }
      });
    }
  }

  private handleImageUpdates(newImages: Array<File>, deletedImages: Array<string>) {
    const imagesRequest: ProductImagesRequest = {
      imgUrlsForDelete: deletedImages,
      newImgList: newImages
    };

    this.productService.updateProductImages(this.editProductId, imagesRequest).subscribe({
      next: () => {
        this.completeEdit(EDIT_SUCCESS_MESSAGE);
      },
      error: () => {
        this.completeEdit(EDIT_PARTIAL_SUCCESS_MESSAGE);
      }
    });
  }

  private completeEdit(message: MockupDataModel) {
    this.redirectToProducts(() => {
      this.showSnackbar(message, SnackBarUtil.CLASSES.success);
    });
  }

  protected initProduct() {
    if (!this.editProductId) return;

    this.productService.getProductRequest(this.editProductId).subscribe({
      next: (productRequest: ProductRequest) => {
        this.productToEdit = this.mapProductRequestToEditableProduct(productRequest);

        const formValues = this.createFormValuesFromProduct(this.productToEdit);
        this.isVisibilityActive = this.productToEdit.availability == ProductAvailability.ACTIVE
        this.addProductFormGroup.patchValue(formValues);

        if (this.colorAttributeValues.length) {
          this.applyProductColors();
        }
      },
      error: () => {
        this.showSnackbar(LOAD_PRODUCT_DATA_ERROR_MESSAGE, SnackBarUtil.CLASSES.error);
      },
    });
  }

  private applyProductColors() {
    this.selectedAttributeValues = this.colorAttributeValues.filter(attr =>
      this.productToEdit?.attributeValueIds?.includes(attr.id)
    );
    this.lastSelectedColor = this.selectedAttributeValues.at(-1);
    this.updateAttributeValueIdsInForm();
  }

  private mapProductRequestToEditableProduct(productRequest: ProductRequest): ProductRequest {
    const translationsMap = new Map<LanguageVariations, ProductTranslation>(
      Object.entries(productRequest.translations) as [LanguageVariations, ProductTranslation][]
    );

    return {
      ...productRequest,
      translations: translationsMap,
    };
  }

  private createFormValuesFromProduct(product: ProductRequest) {
    const enValues = this.getTranslationValues(product, LanguageVariations.EN);
    const ruValues = this.getTranslationValues(product, LanguageVariations.RU);
    const hyValues = this.getTranslationValues(product, LanguageVariations.HY);

    return {
      price: product?.price,
      availability: product?.availability,
      attributeValueIds: product?.attributeValueIds,
      translations: {
        EN: {
          id: enValues?.id,
          productName: enValues?.productName,
          productDescription: enValues?.productDescription,
          compatibilityDescription: enValues?.compatibilityDescription,
        },
        RU: {
          id: ruValues?.id,
          productName: ruValues?.productName,
          productDescription: ruValues?.productDescription,
          compatibilityDescription: ruValues?.compatibilityDescription,
        },
        HY: {
          id: hyValues?.id,
          productName: hyValues?.productName,
          productDescription: hyValues?.productDescription,
          compatibilityDescription: hyValues?.compatibilityDescription,
        },
      },
    };
  }

  protected selectLanguage($event: any) {
    const language = this.stepperLanguageOptions.get($event.selectedIndex);
    if (language) {
      this.selectedLanguageOption = language as Language;
    }
  }

  protected onDrop(event: DragEvent, hiddenInputElement: HTMLInputElement) {
    event.preventDefault();
    if (this.productGalleryUrls.length == MAX_PRODUCT_GALLERY_LENGTH) {
      return;
    }

    if (event.dataTransfer && event.dataTransfer.files) {
      this.handleFiles(event.dataTransfer.files);
      hiddenInputElement.value = '';
    }
  }

  protected onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  protected onClickDropFiles(hiddenInputElement: HTMLInputElement) {
    if (this.productGalleryUrls.length == MAX_PRODUCT_GALLERY_LENGTH) {
      return;
    }

    hiddenInputElement.click();
    hiddenInputElement.onchange = () => {
      this.handleFiles(hiddenInputElement.files);
      hiddenInputElement.value = '';
    };
  }

  protected onClickDeleteImage(imageUrl: string) {
    this.productGalleryMap.delete(imageUrl);
  }

  protected onClickDeleteImageForEdit(imageUrl: string | File) {
    if (this.productToEdit?.imageUrls && this.productToEdit.imageUrls.some(url => url == imageUrl)) {
      this.productToEdit.imageUrls = this.productToEdit.imageUrls.filter(url => url != imageUrl);
      if (!this.productToEdit.deletedImages) {
        this.productToEdit.deletedImages = [];
      }
      if (typeof imageUrl == "string") {
        this.productToEdit.deletedImages.push(imageUrl);
      } else {
        this.productToEdit.imageUrls = this.productToEdit.imageUrls.filter(value => value != imageUrl);
      }
    }
  }

  protected onClickColor(attributeValueResponse: AttributeValueResponse) {
    if (this.selectedAttributeValues.some(attributeValue => attributeValue.id == attributeValueResponse.id)) {
      const index = this.selectedAttributeValues.indexOf(attributeValueResponse);
      if (index !== -1) {
        this.selectedAttributeValues.splice(index, 1);
      }
      this.lastSelectedColor = this.selectedAttributeValues.at(-1);
      this.updateAttributeValueIdsInForm();
    } else if (this.selectedAttributeValues.length == MAX_SELECTED_COLORS) {
      return;
    } else {
      this.lastSelectedColor = attributeValueResponse;
      this.selectedAttributeValues.push(attributeValueResponse);
      this.updateAttributeValueIdsInForm();
    }
  }

  protected updateAttributeValueIdsInForm() {
    const attributeValueIds = this.selectedAttributeValues.map(attributeValue => attributeValue.id);
    this.getFormControlByName('attributeValueIds').setValue(attributeValueIds);
  }

  protected isColorSelected(attributeValueResponse: AttributeValueResponse) {
    return this.selectedAttributeValues.some(attributeValue => attributeValue.id == attributeValueResponse.id);
  }

  protected getFormControlByName(formControlName: string) {
    return this.addProductFormGroup.get(formControlName) as FormControl;
  }

  protected getInvalidValidatorName(formControlName: string): string | null {
    const control = this.getFormControlByName(formControlName);

    if (control && control.errors && (control.touched || control.dirty)) {
      return Object.keys(control.errors)[0];
    }

    return null;
  }

  protected getInvalidValidatorNameByFormControl(control: FormControl<any>): string | null {
    if (control && control.errors && (control.touched || control.dirty)) {
      return Object.keys(control.errors)[0];
    }

    return null;
  }

  protected getFormControlNameByNameAndCurrentLang(formControlName: string) {
    let formLanguageOption = this.getCurrentLanguageFormGroup();
    const translationsFormGroup = this.addProductFormGroup.get('translations');
    const languageFormGroup = translationsFormGroup?.get(formLanguageOption);
    const resultFormControl = languageFormGroup?.get(formControlName);
    return resultFormControl as FormControl;
  }

  protected updateAvailabilityInForm(newValue: boolean) {
    this.isVisibilityActive = newValue;
    const availability = this.isVisibilityActive ? ProductAvailability.ACTIVE : ProductAvailability.INACTIVE;
    this.getFormControlByName('availability')?.setValue(availability);
  }

  protected onKeyPress(event: KeyboardEvent) {
    const priceControl = this.getFormControlByName('price');
    if ((priceControl.value == null || priceControl.value.length == 0) && Number(event.key) < 1) {
      event.preventDefault();
      return;
    }
    const allowedChars = /[^0-9]/g;
    const inputChar = String.fromCharCode(event.charCode);
    if (allowedChars.test(inputChar)) {
      event.preventDefault();
      return;
    }
  }

  private initAllColorAttributeValues() {
    this.attributeValueService.getAll().subscribe({
      next: (attributeValues) => {
        this.colorAttributeValues = attributeValues.filter(attributeValue => attributeValue.attribute.name == COLOR);

        if (this.isEditProductPage && this.productToEdit) {
          this.applyProductColors();
        } else if (!this.isEditProductPage) {
          const firstColor = this.colorAttributeValues.at(0);
          if (firstColor) {
            this.selectedAttributeValues.push(firstColor);
            this.lastSelectedColor = firstColor;
            this.updateAttributeValueIdsInForm();
          }
        }
      }
    })
  }

  private handleFiles(files: FileList | null) {
    if (files && files.length > 0) {
      const verifiedFiles = files.length > 4 ? Array.from(files).splice(0, 4) : files;
      const sizeVerifiedFiles = Array.from(verifiedFiles).filter(file => {
        return this.filterFilesAndRemoveFilesWhereSizeIsOver3MB(file);
      });

      Array.from(sizeVerifiedFiles).forEach(file => {
        this.addFileToProductGalleryMap(file);
      });
    }
  }


  protected getProductImageUrl(image: string | File) {
    if(typeof image == "string") {
      return this.imageService.getImageUrl(image)
    }
    return URL.createObjectURL(image);
  }

  private addFileToProductGalleryMap(file: File) {
    this.productGalleryMap.set(URL.createObjectURL(file), file);
    if (this.isEditProductPage && this.productToEdit) {
      if (!this.productToEdit.newImages) {
        this.productToEdit.newImages = [];
      }
      this.productToEdit.imageUrls.push(file);
      this.productToEdit.newImages.push(file);
    }
  }


  private updateStepByInvalidTranslationGroup() {
    const keyOfInvalidGroup = this.getFirstInvalidTranslationGroup();
    if(keyOfInvalidGroup) {
      this.selectedLanguageOption = keyOfInvalidGroup.toLowerCase() as Language;
    }
  }

  private getTranslationValues(productRequest: ProductRequest, lang: LanguageVariations): ProductTranslation | undefined {
    return productRequest.translations.get(lang);
  }

  private getCurrentLanguageFormGroup() {
    let formLanguageOption: LanguageVariations = LanguageVariations.HY;
    switch (this.selectedLanguageOption) {
      case Language.RU:
        formLanguageOption = LanguageVariations.RU;
        break;
      case Language.EN:
        formLanguageOption = LanguageVariations.EN;
        break;
      case Language.HY:
        formLanguageOption = LanguageVariations.HY;
        break;
    }
    return formLanguageOption;
  }

  private getFirstInvalidTranslationGroup(): string | null {
    const translations = this.addProductFormGroup.get('translations') as FormGroup;

    if (!translations) return null;

    for (const key of Object.keys(translations.controls)) {
      const group = translations.get(key) as FormGroup;
      if (group && group.invalid) {
        return key;
      }
    }

    return null;
  }

  private createFormGroup(): FormGroup {
    return this.fb.group({
      price: [null, Validators.required],
      availability: [ProductAvailability.ACTIVE, Validators.required],
      attributeValueIds: [[], [Validators.maxLength(3), Validators.required]],
      translations: this.fb.group({
        EN: this.createTranslationGroup(),
        RU: this.createTranslationGroup(),
        HY: this.createTranslationGroup()
      })
    });
  }

  private createTranslationGroup() {
    return this.fb.group({
      id: [null],
      productName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(60)]],
      productDescription: ['', Validators.maxLength(1007)],
      compatibilityDescription: ['', Validators.maxLength(607)]
    });
  }

  private filterFilesAndRemoveFilesWhereSizeIsOver3MB(file: File) {
    if (file.size > 3 * 1024 * 1024) {
      this.showImageSizeErrorFor3Seconds();
      return false;
    }
    return true;
  }

  private showImageSizeErrorFor3Seconds() {
    this.showImageSizeError = true;
    setTimeout(() => {
      this.showImageSizeError = false;
    }, 3000);
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBarUtil.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data
    });
  }

  private scrollToSteps() {
    const headerHeight = 200;
    const elementPosition = this.topOfForm.nativeElement.offsetTop;
    const adjustedPosition = elementPosition - headerHeight;

    const options: ScrollToOptions = {
      behavior: 'smooth',
      top: adjustedPosition
    };

    window.scrollTo(options);
  }


  protected readonly ToggleElModel = ToggleElModel;
  protected readonly CK_EDITOR_CONFIG = CK_EDITOR_CONFIG;
  protected readonly CK_EDITOR = CK_EDITOR;
}
